import {Record, Map} from 'immutable';
import {defaults} from 'lodash';
import {ObjectIdOrString} from '@packages/utilities/mongoUtils';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import type {MongoAssetVariant} from '@packages/domains/assets/mongodb';

export type RawFileType = {
  _id: string;
  createdOn: string;
  updatedOn?: string;
  site?: string; // Either site, databaseId, or versionedResource must be defined
  databaseId?: string; // Either site, databaseId, or versionedResource must be defined
  versionedResource?: string | null; // A versioned resource may be linked with a sharedResourceProfile or marketplaceResourceProfile
  sharedResourceProfile?: string | null; // Can be linked with a versionedResource
  marketplaceResourceProfile?: string | null; // Can be linked with a versionedResource
  height?: number | null; // Only image files have height;
  width?: number | null; // Only image files have width;
  size?: number;
  mimeType: string;
  s3Url: string;
  cdnUrl?: string;
  fileHash: string;
  fileName: string;
  origFileName: string;
  variants?: ImageVariantType[] | VideoVariantType[];
  thumbUrl?: string;
  isHD?: boolean;
  type?: 'AUDIO' | 'LOTTIE' | 'FIGMA' | 'RIVE' | 'image' | 'document';
  totalDuration?: number;
  markedAsDeleted?: boolean;
  placeholder?: boolean;
  alt?: string;
  figmaNodeId?: string;
  figmaFileKey?: string;
  figmaFileName?: string;
  figmaScale?: number;
  folderPath?: {id: string; name: string}[] | null;
  isFileUpload?: boolean;
  localizedSettings?: {
    [localeId: string]: {
      alt: string | null;
    };
  };
  translationLoading?: boolean;
  isFont?: boolean;
  origin?: Qualified<ObjectIdOrString> | null;
  isFromWellKnownFolder?: boolean | null;
};

export type ImageVariantType = {
  _id: string;
} & Required<
  Pick<
    MongoAssetVariant,
    | 'origFileName'
    | 'fileName'
    | 'size'
    | 'width'
    | 'height'
    | 'quality'
    | 'format'
    | 's3Url'
  >
> &
  Pick<MongoAssetVariant, 'cdnUrl'>;

export type VideoVariantType = Required<
  Pick<MongoAssetVariant, 'fileName' | 'format' | 'origFileName' | 's3Url'>
> &
  Pick<MongoAssetVariant, 'cdnUrl'>;

export type FileRecordProps = {
  _id: string;
  createdOn: string;
  updatedOn: string | null;
  siteId: string | null;
  databaseId: string | null;
  height: number | null;
  width: number | null;
  fileSize: number;
  mimeType: string;
  s3Url: string;
  cdnUrl: string;
  fileHash: string;
  fileName: string;
  origFileName: string;
  variants: ImageVariantType[] | VideoVariantType[];
  thumbUrl: string | null;
  externalUrl: string | null;
  isHD: boolean | null;
  type: 'AUDIO' | 'LOTTIE' | 'FIGMA' | 'RIVE' | 'image' | 'document' | null;
  totalDuration: number | null;
  markedAsDeleted: boolean | null;
  blob: File | null;
  blobUrl: string;
  uploadProgress: number | null;
  transcodeProgress: number | null;
  processing: boolean;
  placeholder: boolean | null;
  alt: string | null;
  figmaNodeId: string | null;
  figmaFileKey: string | null;
  figmaFileName: string | null;
  figmaScale: number | null;
  folderPath?: {id: string; name: string}[] | null;
  localizedSettings?: {
    [localeId: string]: {
      alt: string | null;
    };
  };
  translationLoading?: boolean;
  origin?: Qualified<ObjectIdOrString> | null;
  isFromWellKnownFolder?: boolean | null;
  versionedResource?: ObjectIdOrString | null;
  sharedResourceProfile?: ObjectIdOrString | null;
  marketplaceResourceProfile?: ObjectIdOrString | null;
};

export const defaultFileRecordValues: FileRecordProps = {
  _id: '',
  createdOn: '',
  updatedOn: null,
  siteId: null,
  databaseId: null,
  height: null,
  width: null,
  fileSize: 0,
  mimeType: '',
  s3Url: '',
  cdnUrl: '',
  fileHash: '',
  fileName: '',
  folderPath: null,
  origFileName: '',
  variants: [],
  thumbUrl: null,
  externalUrl: null,
  isHD: null,
  type: null,
  totalDuration: null,
  markedAsDeleted: null,
  localizedSettings: {},
  translationLoading: undefined,
  alt: null,

  // Metadata for Designer UI. We remove the keys these keys
  // when serializing FileRecords to JSON in toJSON method as
  // we don't want these keys to end up in the server.
  blob: null,
  blobUrl: '',
  uploadProgress: null,
  transcodeProgress: null,
  processing: false,
  placeholder: null,
  figmaNodeId: null,
  figmaFileKey: null,
  figmaFileName: null,
  figmaScale: null,
  origin: null,
  isFromWellKnownFolder: null,
  versionedResource: null,
  sharedResourceProfile: null,
  marketplaceResourceProfile: null,
};

const Parent = Record(defaultFileRecordValues, 'FileRecord');

export class FileRecord extends Parent {
  declare _id: FileRecordProps['_id'];
  declare cdnUrl: FileRecordProps['cdnUrl'];
  declare externalUrl: FileRecordProps['externalUrl'];
  declare createdOn: FileRecordProps['createdOn'];
  declare databaseId: FileRecordProps['databaseId'];
  declare fileName: FileRecordProps['fileName'];
  declare fileHash: FileRecordProps['fileHash'];
  declare fileSize: FileRecordProps['fileSize'];
  declare height: FileRecordProps['height'];
  declare width: FileRecordProps['width'];
  declare isHD: FileRecordProps['isHD'];
  declare type: FileRecordProps['type'];
  declare totalDuration: FileRecordProps['totalDuration'];
  declare markedAsDeleted: FileRecordProps['markedAsDeleted'];
  declare mimeType: FileRecordProps['mimeType'];
  declare origFileName: FileRecordProps['origFileName'];
  declare s3Url: FileRecordProps['s3Url'];
  declare siteId: FileRecordProps['siteId'];
  declare thumbUrl: FileRecordProps['thumbUrl'];
  declare updatedOn: FileRecordProps['updatedOn'];
  declare variants: FileRecordProps['variants'];
  declare blob: FileRecordProps['blob'];
  declare blobUrl: FileRecordProps['blobUrl'];
  declare uploadProgress: FileRecordProps['uploadProgress'];
  declare transcodeProgress: FileRecordProps['transcodeProgress'];
  declare processing: FileRecordProps['processing'];
  declare placeholder: FileRecordProps['placeholder'];
  declare alt: FileRecordProps['alt'];
  declare figmaNodeId: FileRecordProps['figmaNodeId'];
  declare figmaFileKey: FileRecordProps['figmaFileKey'];
  declare figmaFileName: FileRecordProps['figmaFileName'];
  declare figmaScale: FileRecordProps['figmaScale'];
  declare folderPath: FileRecordProps['folderPath'];
  declare origin: FileRecordProps['origin'];
  declare isFromWellKnownFolder: FileRecordProps['isFromWellKnownFolder'];

  static reify(json?: RawFileType | null) {
    if (!json) {
      return new FileRecord();
    }

    const {
      _id,
      createdOn,
      updatedOn,
      site: siteId,
      databaseId,
      height,
      width,
      size: fileSize,
      mimeType,
      s3Url,
      cdnUrl,
      fileHash,
      fileName,
      folderPath,
      origFileName,
      variants,
      thumbUrl,
      isHD,
      type,
      totalDuration,
      markedAsDeleted,
      placeholder,
      alt,
      figmaNodeId,
      figmaFileKey,
      figmaFileName,
      figmaScale,
      localizedSettings = {},
      translationLoading = false,
      origin,
      isFromWellKnownFolder = false,
      versionedResource,
      sharedResourceProfile,
      marketplaceResourceProfile,
    } = json;

    return new FileRecord(
      defaults(
        {
          _id,
          createdOn,
          updatedOn,
          siteId,
          databaseId,
          height,
          width,
          fileSize,
          mimeType,
          s3Url,
          cdnUrl,
          fileHash,
          fileName,
          folderPath,
          origFileName,
          variants,
          thumbUrl,
          isHD,
          type,
          totalDuration,
          markedAsDeleted,
          placeholder,
          alt,
          figmaNodeId,
          figmaFileKey,
          figmaFileName,
          figmaScale,
          localizedSettings,
          translationLoading,
          origin,
          isFromWellKnownFolder,
          versionedResource,
          sharedResourceProfile,
          marketplaceResourceProfile,
        },
        defaultFileRecordValues
      )
    );
  }

  // Revert reification
  toRawFileType(): RawFileType {
    return {
      ...this.toSeq()
        .toMap()
        .update(cleanUpNonServerValues)
        .delete('siteId')
        .delete('fileSize')
        .toJSON(),
      site: this.siteId,
      size: this.fileSize,
    };
  }

  toJSON() {
    return this.toSeq().toMap().update(cleanUpNonServerValues).toJSON();
  }
}

const cleanUpNonServerValues = (
  fileRecordAsMap: Map<keyof FileRecordProps, any>
) => {
  return fileRecordAsMap.withMutations((mutable) => {
    fileRecordAsMap.forEach((value, key) => {
      if (key && isNonServerValue(key, value)) {
        mutable.delete(key);
      }
    });
  });
};

const isNonServerValue = (key: keyof FileRecordProps, value: any) => {
  return (
    isDefaultBlankValue(key, value) ||
    (key === 'thumbUrl' && value.startsWith('blob:')) ||
    NON_SERVER_KEYS.includes(key)
  );
};

const isDefaultBlankValue = (key: keyof FileRecordProps, value: unknown) => {
  return value === defaultFileRecordValues[key];
};

const NON_SERVER_KEYS = [
  'uploadProgress',
  'transcodeProgress',
  'processing',
  'placeholder',
  'blob',
  'blobUrl',
];

// FileRecord is a Record subclass so we
// use the nominal type of the subclass.
export type FileRecordType = FileRecord;
